﻿import { useSections } from "@/shared/selectors/SectionsOfPartSelector";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

type Props = {
	open: boolean;
	onClose: () => void;
	onSave: (selectedSectionIds: number[]) => void;
	currentSectionIds: string | null;
	passNumber: number | null;
};

export const PassesSectionsModal = ({
	open,
	onClose,
	onSave,
	currentSectionIds,
	passNumber,
}: Props) => {
	const [error, setError] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useSections({
		onError: (error) => setError(error.message),
	});

	const handleToggleSection = (sectionId: number) => {
		const newSelectedIds = new Set(selectedIds);
		if (newSelectedIds.has(sectionId)) {
			newSelectedIds.delete(sectionId);
		} else {
			newSelectedIds.add(sectionId);
		}
		setSelectedIds(newSelectedIds);
	};

	const handleSave = () => {
		onSave(Array.from(selectedIds));
		onClose();
	};

	const handleCancel = () => {
		setSearchTerm("");
		onClose();
	};

	useEffect(() => {
		if (open && currentSectionIds) {
			const ids = currentSectionIds
				.split(",")
				.map((id) => parseInt(id.trim()))
				.filter((id) => !isNaN(id));
			setSelectedIds(new Set(ids));
		} else if (open) {
			setSelectedIds(new Set());
		}
	}, [open, currentSectionIds]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	const filteredSections = data?.sections.filter(
		(section) =>
			section.SectionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			section.SectionFamilyName.toLowerCase().includes(
				searchTerm.toLowerCase(),
			),
	);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={handleCancel}
				maxWidth="md"
				title={`Edit Sections for Pass ${passNumber}`}
				subtitle="Select sections that can use this pass"
			>
				<MesfModal.Content dividers sx={{ minWidth: "45vw " }}>
					<Box sx={{ mb: 2 }}>
						<TextField
							fullWidth
							size="small"
							label="Search sections or families"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							autoFocus
							slotProps={{
								htmlInput: { "data-test-id": "search-sections" },
							}}
						/>
					</Box>

					{isLoading ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								minHeight: 200,
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<TableContainer sx={{ height: 400 }}>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox" width="10%">
											<Checkbox
												checked={selectedIds.size == filteredSections?.length}
												onChange={() => {
													if (selectedIds.size > 0) {
														setSelectedIds(new Set());
													} else {
														setSelectedIds(
															new Set(
																filteredSections?.map(
																	(section) => section.SectionId!,
																),
															),
														);
													}
												}}
												data-test-id="selectAll"
											/>
										</TableCell>
										<TableCell width="25%">Section</TableCell>
										<TableCell width="25%">Family</TableCell>
										<TableCell width="20%">TPH</TableCell>
										<TableCell width="20%">Target TPH</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredSections && filteredSections.length > 0 ? (
										filteredSections.map((section) => (
											<TableRow
												key={section.SectionId}
												hover
												onClick={() =>
													section.SectionId &&
													handleToggleSection(section.SectionId)
												}
												sx={{ cursor: "pointer" }}
												data-test-id={`section-row-${section.SectionName.toLowerCase()}`}
											>
												<TableCell padding="checkbox">
													<Checkbox
														checked={
															section.SectionId
																? selectedIds.has(section.SectionId)
																: false
														}
														onChange={() =>
															section.SectionId &&
															handleToggleSection(section.SectionId)
														}
													/>
												</TableCell>
												<TableCell>{section.SectionName}</TableCell>
												<TableCell>
													<Typography variant="body2" color="textSecondary">
														{section.SectionFamilyName}
													</Typography>
												</TableCell>
												<TableCell>
													{section.TonsPerHour?.toFixed(2) || "-"}
												</TableCell>
												<TableCell>
													{section.TargetTPH?.toFixed(2) || "-"}
												</TableCell>
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={5} align="center">
												<Typography variant="body2" color="textSecondary">
													No sections found
												</Typography>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}

					<Box sx={{ mt: 2 }}>
						<Typography variant="body2" color="textSecondary">
							{selectedIds.size} section(s) selected
						</Typography>
					</Box>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={handleCancel} color="inherit">
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						data-test-id="save-sections"
					>
						Save
					</Button>
				</MesfModal.Actions>
			</MesfModal>

			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading sections"
			/>
		</>
	);
};
